import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import type { ReactNode } from 'react';

// ─── Fallback UI ────────────────────────────────────────────────────────────
function ErrorFallback({
  error,
  resetError,
}: {
  error: Error;
  resetError: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="text-5xl">🎸</div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Algo salió mal</h1>
          <p className="text-muted-foreground text-sm">
            Ocurrió un error inesperado. El equipo de Resuena fue notificado automáticamente.
          </p>
        </div>

        {import.meta.env.DEV && error?.message && (
          <pre className="text-left text-xs bg-muted text-muted-foreground rounded-lg p-4 overflow-auto max-h-40">
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={resetError}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            Reintentar
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent transition-colors"
          >
            Ir al inicio
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ErrorBoundary wrapper ───────────────────────────────────────────────────
interface ErrorBoundaryProps {
  children: ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback
          error={error as Error}
          resetError={resetError}
        />
      )}
      onError={(error, componentStack) => {
        // Sentry already captures the event via SentryErrorBoundary;
        // this hook is available for additional logging if needed.
        if (import.meta.env.DEV) {
          console.error('[ErrorBoundary]', error, componentStack);
        }
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
}
